import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { digitalApi as api } from './api';
import { useAuth } from '../../context/AuthContext';
import { C } from '../../theme';
import { useToast } from '../../components/Toast';
import DigitalPlanningEngine from './DigitalPlanningEngine';
import { FileText, Search, CheckCircle, Archive, Loader2, Copy } from 'lucide-react';

const STATUS_VARIANT = { draft:'neutral', review:'warning', approved:'success', archived:'neutral' };
const STATUS_ICON    = { draft:FileText, review:Search, approved:CheckCircle, archived:Archive };
const SIcon = ({s}) => { const I = STATUS_ICON[s]; return I ? <I size={11} style={{verticalAlign:'middle',marginRight:3}}/> : null; };

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(80,120,160,0.25)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }} onClick={onClose}>
      <div style={{...C.modal, width:420}} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:800, color:'#1a2a3a' }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#8aa0b8', fontSize:22, cursor:'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function DigitalPlanEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit, canApprovePlans, canCreatePlans, isAdmin } = useAuth();
  const { showToast } = useToast();

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cloneConfirm, setCloneConfirm] = useState(false);
  const [cloning, setCloning] = useState(false);

  const load = useCallback(() => {
    api.getPlan(id).then(p => { setPlan(p); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = useCallback(async (engineData) => {
    setSaving(true);
    try {
      await api.savePlan(id, { data: engineData });
      setPlan(p => ({ ...p, data: engineData }));
      showToast('Plan saved', 'success');
    } catch(e) {
      showToast('Save failed — try again', 'danger');
    } finally {
      setSaving(false);
    }
  }, [id, showToast]);

  async function handleCloneConfirmed() {
    setCloning(true);
    try {
      const clone = await api.clonePlan(id);
      setCloneConfirm(false);
      navigate(`/plans/${clone.id}`);
    } catch (e) { showToast(e.message, 'danger'); setCloning(false); }
  }

  async function handleStatusAction(action) {
    if (!window.confirm(`${action} this plan?`)) return;
    try {
      if (action === 'Submit for Review') await api.submitPlan(id);
      if (action === 'Approve')           await api.approvePlan(id);
      if (action === 'Reject to Draft')   await api.rejectPlan(id);
      if (action === 'Archive')           await api.archivePlan(id);
      load();
    } catch(e) { alert(e.message); }
  }

  const isReadOnly = !canEdit ||
    (plan?.status === 'approved' && !isAdmin) ||
    plan?.archived;

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', flex:1, minHeight:0, background:'linear-gradient(135deg,#e8eef5,#d8e8f2)', color:'#8aa0b8', fontFamily:"'Inter',sans-serif" }}>
      Loading plan…
    </div>
  );

  if (!plan) return (
    <div style={{ padding:40, color:'#a02020', fontFamily:"'Inter',sans-serif" }}>Plan not found.</div>
  );

  // Found 2026-10-04 (building the plan Catalogue view): this used to be a hard either/or —
  // `plan.data` wholesale, or a hardcoded empty-plan default, chosen purely on whether any
  // flights existed yet. A plan with real catalogue/audience edits (e.g. from the new per-field
  // Catalogue view) but zero flights — a completely normal state for a brand-new plan a planner
  // hasn't added a flight to yet — had those edits silently discarded on every reload. Now always
  // starts from the same defaults and overlays whatever `plan.data` actually has, so a truly
  // never-touched plan (`data` defaults to `{}`) behaves identically to before, while a plan with
  // saved catalogue/universe/audience data but no flights keeps it.
  const initialData = { year: plan.year || 2026, universe: 531192, brands: [], platforms: [], ...plan.data, planName: plan.name };

  return (
    <div style={{ display:'flex', flexDirection:'column', flex:1, minHeight:0, overflow:'hidden' }}>
      {/* Status bar */}
      <div style={{
        display:'flex', alignItems:'center', gap:10, padding:'5px 16px',
        background:'rgba(255,255,255,0.9)', backdropFilter:'blur(12px)',
        borderBottom:`1px solid #dce8f0`, flexShrink:0,
        fontFamily:"'Inter',sans-serif",
      }}>
        <button onClick={() => navigate(-1)} style={{
          background:'transparent', border:'1px solid #dce8f0', borderRadius:6,
          padding:'4px 10px', fontSize:12, color:'#4a6a8a', cursor:'pointer'
        }}>← Back</button>

        <div style={{ width:1, height:20, background:'#dce8f0' }}/>

        <span style={{ fontSize:13, fontWeight:700, color:'#1a2a3a' }}>{plan.name}</span>
        <span style={C.statusRow()}>
          <span style={C.statusDot(STATUS_VARIANT[plan.status]||'neutral')}/>
          {plan.status}
        </span>
        <span style={{ fontSize:11, color:'#8aa0b8' }}>v{plan.version||1}</span>

        {saving && <span style={{ fontSize:11, color:'#8aa0b8', display:'inline-flex', alignItems:'center', gap:4 }}><Loader2 size={11} style={{animation:'spin 1s linear infinite'}}/>Saving…</span>}

        <div style={{ flex:1 }}/>

        {plan.status==='draft' && canEdit && (
          <button onClick={()=>handleStatusAction('Submit for Review')} style={{
            background:'transparent', border:`1px solid #d0800044`, borderRadius:6,
            padding:'4px 10px', fontSize:11, color:'#d08000', cursor:'pointer'
          }}>Submit for Review</button>
        )}
        {plan.status==='review' && canApprovePlans && (<>
          <button onClick={()=>handleStatusAction('Approve')} style={{
            background:'transparent', border:`1px solid #2a8a5a44`, borderRadius:6,
            padding:'4px 10px', fontSize:11, color:'#2a8a5a', cursor:'pointer'
          }}>✓ Approve</button>
          <button onClick={()=>handleStatusAction('Reject to Draft')} style={{
            background:'transparent', border:`1px solid #a0202044`, borderRadius:6,
            padding:'4px 10px', fontSize:11, color:'#a02020', cursor:'pointer'
          }}>✗ Reject</button>
        </>)}
        {isAdmin && !plan.archived && (
          <button onClick={()=>handleStatusAction('Archive')} style={{
            background:'transparent', border:'1px solid #dce8f0', borderRadius:6,
            padding:'4px 10px', fontSize:11, color:'#8aa0b8', cursor:'pointer'
          }}>Archive</button>
        )}
        {canCreatePlans && (
          <button onClick={()=>setCloneConfirm(true)} style={{
            background:'transparent', border:'1px solid #dce8f0', borderRadius:6,
            padding:'4px 10px', fontSize:11, color:'#4a6a8a', cursor:'pointer',
            display:'inline-flex', alignItems:'center', gap:5,
          }}><Copy size={11}/>Clone</button>
        )}
      </div>

      {cloneConfirm && (
        <Modal title="Clone Plan" onClose={()=>!cloning && setCloneConfirm(false)}>
          <p style={{ margin:'0 0 16px', color:'#1a2a3a', fontSize:14 }}>
            Clone <strong>{plan.name}</strong>? Only the saved version is copied — any unsaved changes in this editor are not included. The new plan is a draft under this brand.
          </p>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={C.btnSecondary} onClick={()=>setCloneConfirm(false)} disabled={cloning}>Cancel</button>
            <button style={C.btnPrimary} onClick={handleCloneConfirmed} disabled={cloning}>{cloning ? 'Cloning…' : 'Clone'}</button>
          </div>
        </Modal>
      )}

      <div style={{ flex:1, overflow:'hidden' }}>
        <DigitalPlanningEngine
          initialData={initialData}
          planName={plan.name}
          onSave={handleSave}
          readOnly={isReadOnly}
          clientId={plan.client_id}
          planStatus={plan.status}
          planId={plan.id}
          brandId={plan.brand_id}
          clientName={plan.client_name}
        />
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
